import { useState } from 'react';
import { Show } from '@refinedev/antd';
import { useCan, useDelete, useGo, useShow } from '@refinedev/core';
import {
  Descriptions,
  Tag,
  Button,
  Space,
  Popconfirm,
  Input,
  Alert,
} from 'antd';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BidiText, MonoCode, formatDateBidi } from '@lebinatikra/rtl-utils';
import { FilterApiError } from '../../lib/filter-api-client';

interface OrgRow {
  id: string;
  community_id: string;
  name: string;
  slug: string;
  billing_contact: string | null;
  subscription_tier: string;
  created_at: string;
}

export function OrganizationShow() {
  const { t, i18n } = useTranslation();
  const { query } = useShow<OrgRow>();
  const record = query.data?.data;

  const can = useCan({ resource: 'organizations', action: 'delete' });
  const canDelete = can.data?.can === true;

  const { mutate: deleteMutate, mutation } = useDelete();
  const isDeleting = mutation.isPending;
  const go = useGo();
  const [reason, setReason] = useState('');

  const onConfirm = () => {
    if (!record) return;
    deleteMutate(
      {
        resource: 'organizations',
        id: record.id,
        values: reason.trim() ? { reason: reason.trim() } : undefined,
        mutationMode: 'pessimistic',
        successNotification: () => ({
          message: t('notifications.organization_deleted'),
          type: 'success',
        }),
        errorNotification: (err) => {
          const httpErr = err as { errors?: { code?: string } } | undefined;
          if (httpErr?.errors?.code === 'HAS_ACTIVE_DEPENDENCIES') {
            return { message: t('errors.has_active_dependencies'), type: 'error' };
          }
          if (err instanceof FilterApiError) {
            return {
              message: i18n.language === 'en' ? err.messageEn : err.message,
              type: 'error',
            };
          }
          return { message: t('errors.generic'), type: 'error' };
        },
      },
      {
        onSuccess: () => {
          go({ to: `/communities/${record.community_id}/organizations` });
        },
        onError: () => {},
      },
    );
  };

  return (
    <Show isLoading={query.isLoading}>
      {record ? (
        <>
          <Descriptions column={1} bordered>
            <Descriptions.Item label={t('common.id')}>
              {/* rtl-utils:bidi-wrapped */}
              <MonoCode>{record.id}</MonoCode>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.name')}>
              {/* rtl-utils:bidi-wrapped */}
              <BidiText>{record.name}</BidiText>
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.slug')}>
              {/* rtl-utils:bidi-wrapped */}
              <MonoCode>{record.slug}</MonoCode>
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.subscription_tier')}>
              <Tag>{record.subscription_tier}</Tag>
            </Descriptions.Item>
            {record.billing_contact ? (
              <Descriptions.Item label={t('fields.billing_contact')}>
                {/* rtl-utils:bidi-wrapped */}
                <BidiText dir="ltr">{record.billing_contact}</BidiText>
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item label={t('common.created_at')}>
              {formatDateBidi(record.created_at)}
            </Descriptions.Item>
          </Descriptions>
          <Space style={{ marginTop: 16 }}>
            <Link to={`/organizations/${record.id}/groups`}>
              <Button type="primary">{t('resources.groups')}</Button>
            </Link>
            {canDelete ? (
              <Popconfirm
                title={t('actions.delete_organization')}
                description={
                  <Space direction="vertical" style={{ width: 320 }}>
                    <Alert
                      type="warning"
                      showIcon
                      message={t('confirm.soft_delete_warning')}
                    />
                    <Input.TextArea
                      data-testid="soft-delete-reason"
                      placeholder={t('confirm.soft_delete_reason_label')}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      maxLength={500}
                    />
                  </Space>
                }
                onConfirm={onConfirm}
                okButtonProps={{ loading: isDeleting, danger: true }}
                okText={t('common.delete')}
                cancelText={t('common.cancel')}
              >
                <Button danger data-testid="delete-organization-btn">
                  {t('actions.delete_organization')}
                </Button>
              </Popconfirm>
            ) : null}
          </Space>
        </>
      ) : null}
    </Show>
  );
}
